import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DateFormatter, openInNewTab } from 'infrastructure/functions';
import { formatTime } from 'infrastructure/functions/format-time';
import { DateFormats } from 'infrastructure/enums';
import {
  CaretRightOutlined,
  PhoneFilled,
  MessageOutlined,
} from '@ant-design/icons';
import CollapseAnimation from 'components/atoms/base-animations/collapse';
import BaseLoader from 'components/atoms/base-loader';
import { useGetAudioComment } from 'infrastructure/hooks/comments/use-get-audio';
import { Tag } from 'antd';

import s from './styles.module.scss';
import GeneralCommentsListMessageItem from './message-item';

import type { Comment } from 'infrastructure/classes/comments/comment';

interface IGeneralCommentsListCallItemProps {
  data: Comment;
}

const GeneralCommentsListCallItem: React.FC<
  IGeneralCommentsListCallItemProps
> = ({ data }) => {
  const [opened, setOpened] = useState(false);
  const { t } = useTranslation();
  const { loading, getAudioCommentUrl } = useGetAudioComment(data.patientGuid);
  const hasAudio = !!data.duration;

  const toggleOpen = useCallback(() => setOpened((prev) => !prev), []);

  const openCall = async () => {
    if (!data.message || !hasAudio) return;
    const res = await getAudioCommentUrl(data.message);
    if (res) openInNewTab(res.url);
  };

  const callBody = (
    <div className={s['call-item-body']}>
      <button
        onClick={openCall}
        className={s['call-item-body-btn']}
        disabled={!hasAudio || loading}
      >
        <div className={s['call-item-body-icon']}>
          {loading ? (
            <BaseLoader loading width={18} height={18} />
          ) : (
            <CaretRightOutlined />
          )}
        </div>
        <p className={s['call-item-body-message']}>
          {hasAudio ? data.message : t('labels.attemptedToCall')}
        </p>
        {hasAudio && (
          <span className={s.duration}>{formatTime(data.duration)}</span>
        )}
      </button>
    </div>
  );

  const waitingCallBody = (
    <div className={s['call-item-body-waiting']}>
      <p>{t('labels.waitingForCallDetails')}...</p>
    </div>
  );

  return (
    <div className={s['call-item']} data-cy="general-call-comment-item">
      <div className={s['call-item-wrapper']}>
        <div className={s['call-item-header']}>
          <Tag
            icon={<PhoneFilled />}
            color={!hasAudio && !data.hidden ? 'error' : 'success'}
          >
            {t('labels.call')}
          </Tag>
        </div>
        {data.hidden ? waitingCallBody : callBody}
        <div className={s['call-item-footer']} data-cy="call-comment">
          <p className={s['call-item-signed-by']}>
            <span>{t('labels.callBy')}:</span>
            <span>{data.createdFullName}</span>
            <span> • </span>
            <span className={s['call-item-date']}>
              {DateFormatter({
                date: data.createdAt,
                format: DateFormats['MM/dd/yy, hh:mm a'],
              })}
            </span>
          </p>
        </div>
        {!!data.callComments?.length && (
          <>
            <button
              className={s['call-item-comments-btn']}
              onClick={toggleOpen}
            >
              <MessageOutlined />
              {`${data.callComments.length} ${t('labels.callComments')}`}
            </button>
            <CollapseAnimation opened={opened}>
              <ul className={s['call-item-comments-list']}>
                {data.callComments.map((el) => (
                  <li key={el.guid}>
                    <GeneralCommentsListMessageItem data={el} />
                  </li>
                ))}
              </ul>
            </CollapseAnimation>
          </>
        )}
      </div>
    </div>
  );
};

export default GeneralCommentsListCallItem;
